import { type FC, type MouseEvent, useContext, useState } from "react";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { FormattedMessage } from "react-intl";
import { Authorized } from "@packsize/authentication";
import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";

import { ProductionGroupContext } from "@/contexts/production-group";
import { ProductionContext } from "@/contexts/production";
import { RemoveMachineGroupFromProductionGroupModal } from "@/views/production/components/shared-modals";
import { ProductionGroupStatus } from "@/views/production/components/location-control/production-group-status";
import { MachineGroupStatus } from "@/views/production/components/location-control/machine-group-status";

export const LocationControl: FC = () => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [isChangePGModalOpened, setIsChangePGModalOpened] = useState(false);
	const { removeMachineGroupFromProductionGroup } = useContext(ProductionGroupContext);
	const { setCurrentMachineGroup, setCurrentProductionGroup, currentProductionGroup, currentMachineGroup } =
		useContext(ProductionContext);

	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	if (currentMachineGroup === null && currentProductionGroup === null) {
		return null;
	}

	return (
		<>
			<RemoveMachineGroupFromProductionGroupModal
				open={isChangePGModalOpened}
				machineGroup={currentMachineGroup}
				onConfirm={removeMachineGroupFromProductionGroup}
				onCancel={() => setIsChangePGModalOpened(false)}
			/>
			<Stack
				direction="row"
				spacing={2}
				sx={{
					minHeight: 52,
				}}
				data-testid="location-control__stack"
			>
				<Button
					size="small"
					variant="outlined"
					color="info"
					onClick={handleClick}
					endIcon={<KeyboardArrowDownRoundedIcon />}
					sx={{
						minWidth: 200,
						justifyContent: "space-between",
					}}
					data-testid="location-control__button__location-selector"
				>
					{(currentMachineGroup || currentProductionGroup)?.alias ?? ""}
				</Button>
				<Menu
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center",
					}}
					data-testid="location-control__menu"
				>
					<MenuItem
						onClick={() => {
							setCurrentMachineGroup(null);
							setCurrentProductionGroup(null);
							handleClose();
						}}
						sx={{
							height: 60,
						}}
						data-testid="location-control__menu-item__change-location"
					>
						<ListItemIcon
							sx={{
								alignItems: "center",
							}}
						>
							<ExitToAppRoundedIcon />
						</ListItemIcon>
						<ListItemText
							primary={
								<Typography>
									<FormattedMessage id="Change your location" defaultMessage="Change your location" />
								</Typography>
							}
						/>
					</MenuItem>

					{currentMachineGroup !== null && (
						<Authorized permissions="changeMachineGroupsProductionGroup">
							<Divider />
							<MenuItem
								onClick={() => {
									setIsChangePGModalOpened(true);
									handleClose();
								}}
								sx={{ flexDirection: "column", alignItems: "flex-start" }}
								data-testid="location-control__menu-item__change-production-group"
							>
								<Typography>
									<FormattedMessage id="Change Production Group" defaultMessage="Change Production Group" />
								</Typography>
								<Typography variant="caption" sx={{ textTransform: "none" }} color="textSecondary">
									<FormattedMessage
										id="Move selected MG to different PG"
										defaultMessage="Move selected MG to different PG"
									/>
								</Typography>
							</MenuItem>
						</Authorized>
					)}
				</Menu>

				{currentProductionGroup !== null && currentMachineGroup === null && (
					<ProductionGroupStatus selectedValue={currentProductionGroup} />
				)}

				{currentMachineGroup !== null && <MachineGroupStatus selectedValue={currentMachineGroup} />}
			</Stack>
		</>
	);
};
